import i18n from "@microdoing/shared/i18n";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import theme from "app/Theme/Theme";
import normalize from "app/utils/normalize";
import React, { useEffect } from "react";
import { StyleSheet, Pressable, Platform, Dimensions } from "react-native";
import { PanGestureHandler } from "react-native-gesture-handler";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from "react-native-reanimated";
import { widthPercentageToDP } from "react-native-responsive-screen";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import CheckIcon from "../Icons/CheckIcon";
import { Box } from "./Box";
import { MainText } from "./MainText";

const AnimatedBox = Animated.createAnimatedComponent(Box);

const TOAST_HEIGHT = normalize(66);

const styles = StyleSheet.create({
  main: {
    height: TOAST_HEIGHT,
    minHeight: TOAST_HEIGHT,
    overflow: "hidden",
    zIndex: 200,
    position: "absolute",
    top: 0,
    right: 0,
    left: isWebDesktop ? 25 : theme.spacing.xs,
    transition: "easeIn",
    minWidth: widthPercentageToDP(10),
    width: isWebDesktop ? 350 : widthPercentageToDP(100) - theme.spacing.xs * 2,
    backgroundColor: theme.colors["neutral.white"],
    shadowColor: "rgba(220, 221, 227, 1)",
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.18,
    shadowRadius: 16,
    elevation: 1,
    borderWidth: 1,
    borderColor: theme.colors["neutral.100"],
    borderRadius: 6,
    flexDirection: "column",
    alignSelf: "stretch",
    justifyContent: "center",
    flex: 1,
  },
});

interface Props {
  title: string;
  message?: string;
  onClose: () => void;
}

const Toast = ({ title = "", message = "", onClose }: Props) => {
  const translateY = useSharedValue(-TOAST_HEIGHT);
  const progress = useSharedValue(0);
  const insets = useSafeAreaInsets();

  const animatedStyle = useAnimatedStyle(() => {
    return {
      transform: [{ translateY: translateY.value }],
    };
  });

  const animatedProgressStyle = useAnimatedStyle(() => {
    return {
      width: `${progress.value}%`,
    };
  });

  const handleGesture = evt => {
    const { nativeEvent } = evt;

    if (nativeEvent.velocityY > 0) {
      onClose();
    } else {
      onClose();
    }
  };

  useEffect(() => {
    if (title) {
      translateY.value = withSpring(
        Platform.OS === "web" ? 20 : insets.top ? insets.top : theme.spacing.sm,
        {},
        () => {
          progress.value = withTiming(100, { duration: 1200 });
        },
      );
    } else {
      translateY.value = withSpring(
        -TOAST_HEIGHT,
        {},
        () => (progress.value = 0),
      );
    }
  }, [title]);

  return (
    <Animated.View
      style={[
        styles.main,
        animatedStyle,
        Platform.OS === "web" && {
          transform: [{ translateY: !!title ? 0 : -TOAST_HEIGHT }],
        },
      ]}
    >
      <PanGestureHandler onGestureEvent={handleGesture}>
        <Pressable
          onPress={onClose}
          style={{ flexDirection: "row", padding: theme.spacing.s }}
        >
          <Box
            borderRadius={4}
            height={normalize(42)}
            width={normalize(42)}
            justifyContent="center"
            alignItems="center"
            backgroundColor="system.green.100"
          >
            <CheckIcon color={theme.colors["system.green.900"]} />
          </Box>
          <Box
            marginLeft="s"
            flexDirection="column"
            marginRight="s"
            maxWidth="80%"
          >
            <MainText numberOfLines={1} variant="l5-bold" color="neutral.900">
              {i18n.t("success")}
            </MainText>
            <MainText numberOfLines={1} variant="l4-medium" color="neutral.600">
              {title}
            </MainText>
          </Box>
        </Pressable>
      </PanGestureHandler>
      <AnimatedBox
        borderRadius={4}
        position="absolute"
        zIndex={1}
        bottom={0}
        height={4}
        backgroundColor="system.green.900"
        style={animatedProgressStyle}
      />
      <Box
        borderRadius={4}
        position="absolute"
        bottom={0}
        width={widthPercentageToDP(100) - theme.spacing.xs * 2}
        height={4}
        backgroundColor="system.green.100"
      />
    </Animated.View>
  );
};

export default Toast;
